/**
 * MotionEditModal — edit a Motion's name from the Motion Detail page header.
 *
 * Scope: MVP only exposes the `name` field. UpdateMotion supports more fields
 * (industry / assetIds / profile / playbook / kpiTargets) but this dialog is
 * targeted at the "Edit" action next to Refresh — the broader edit surface
 * lives elsewhere.
 */

import { useState } from 'react';
import {
  Box,
  Button,
  FormField,
  Input,
  Modal,
  SpaceBetween,
} from '@amzn/awsui-components-react';
import {
  getIvtClient,
  UpdateMotionCommand,
} from '../../../client/IvtHubClient';
import { denialHint } from '../../../utils/authorization';

interface MotionEditModalProps {
  motionId: string;
  currentName: string;
  onClose: () => void;
  onSaved: (newName: string) => void;
}

export default function MotionEditModal({
  motionId,
  currentName,
  onClose,
  onSaved,
}: MotionEditModalProps) {
  const [name, setName] = useState(currentName);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmed = name.trim();
  const canSubmit = !submitting && !!trimmed && trimmed !== currentName;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const out = await getIvtClient().send(
        new UpdateMotionCommand({ motionId, name: trimmed }),
      );
      const savedName = out.motion?.name ?? trimmed;
      onSaved(savedName);
      onClose();
    } catch (e) {
      setError(denialHint(e) ?? ((e as Error).message || '保存失败'));
      setSubmitting(false);
    }
  };

  return (
    <Modal
      visible
      onDismiss={() => {
        if (!submitting) onClose();
      }}
      header="Edit Motion"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={submitting}
              disabled={!canSubmit}
            >
              Save
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        {error && (
          <Box color="text-status-error" data-testid="motion-edit-error">
            ⚠️ {error}
          </Box>
        )}
        <FormField label="Motion Name">
          <Input
            value={name}
            onChange={(e) => setName(e.detail.value)}
            placeholder="Motion name"
            disabled={submitting}
            autoFocus
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
}
